import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../api/db';
import { useAuth } from '../context/AuthContext';
import ArticleCard from '../components/common/ArticleCard';
import Sidebar from '../components/layout/Sidebar';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiPlus } = FiIcons;

const BlogList = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 12;
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const [arts, cats] = await Promise.all([db.getArticlesWithCategory(true), db.getCategories()]);
      setArticles(arts);
      setCategories(cats);
    };
    load();
  }, []);

  const filteredArticles = useMemo(() => {
    let result = articles;
    if (selectedCategoryId) {
      result = result.filter(a => a.category?.id === selectedCategoryId);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.summary.toLowerCase().includes(q)
      );
    }
    return result;
  }, [articles, selectedCategoryId, searchQuery]);

  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);
  const displayedArticles = filteredArticles.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleCategorySelect = (id) => {
    setSelectedCategoryId(id);
    setPage(1);
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="mb-8">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">Blog Directory</h1>
          <p className="text-xl text-gray-600 max-w-2xl mb-6">
            Browse all our articles, guides, and community stories.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center mb-6">
            <div className="relative flex-1 max-w-xl">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                placeholder="Search articles by title or summary..."
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] bg-white"
              />
            </div>
            {isAuthenticated && (
              <button
                onClick={() => navigate('/admin/editor/new')}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-medium transition-colors hover:opacity-90"
                style={{ backgroundColor: 'var(--brand-primary)' }}
              >
                <SafeIcon icon={FiPlus} /> Add blog
              </button>
            )}
          </div>

          {/* Category Filter Bar */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => handleCategorySelect('')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategoryId === ''
                  ? 'text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
              style={selectedCategoryId === '' ? { backgroundColor: 'var(--brand-primary)' } : {}}
            >
              All
            </button>
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategoryId === category.id
                    ? 'text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                }`}
                style={selectedCategoryId === category.id ? { backgroundColor: 'var(--brand-primary)' } : {}}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main Content (3/4 on lg screens) */}
          <div className="lg:w-3/4 min-h-[400px]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedArticles.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center gap-2">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                      page === i + 1
                        ? 'text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                    style={page === i + 1 ? { backgroundColor: 'var(--brand-primary)' } : {}}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}

            {filteredArticles.length === 0 && (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
                <p className="text-gray-500 text-lg">No articles found.</p>
                <button
                  onClick={() => { setSearchQuery(''); handleCategorySelect(''); }}
                  className="mt-4 font-medium hover:underline"
                  style={{ color: 'var(--brand-primary)' }}
                >
                  View all articles
                </button>
              </div>
            )}
          </div>

          {/* Sidebar (1/4 on lg screens) */}
          <div className="lg:w-1/4">
            <div className="sticky top-28">
              <Sidebar />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BlogList;